import React, { useState, useEffect } from "react";
import { API_BASE_URL, enhancedFetch } from "../services/api";

const BatchImport = ({ token, setSuccess, setError }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [passwordDownloadUrl, setPasswordDownloadUrl] = useState(null);
  
  const [nflg, setNflg] = useState(false);
  const handleSuccess = (msg) => {
    setSuccess(msg);
    setNflg(true);
  }
  const handleError = (err) => {
    setError(err);
    setNflg(true);
  }
  useEffect(() => {
    if(nflg) {
      const timer = setTimeout(()=>{
        setSuccess(null);
        setError(null);
        setNflg(false);
      },5000);
      return () => clearTimeout(timer);
    }
  },[nflg])
  // 批量任务列表相关状态
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [activeTab, setActiveTab] = useState('import'); // 'import' 或 'tasks'
  const [taskDetail, setTaskDetail] = useState(null); // 任务详情
  const [loadingTaskDetail, setLoadingTaskDetail] = useState(false);
  
  // 分页相关状态
  const [pagination, setPagination] = useState({
    page: 1,
    size: 6,
    total: 0,
    hasNext: false,
    hasPrev: false
  });

  // 轮询检查任务状态
  useEffect(() => {
    let intervalId;
    
    if (taskId && processing) {
      intervalId = setInterval(async () => {
        try {
          const response = await enhancedFetch(`${API_BASE_URL}/scripts/batch-import-status/${taskId}`, {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            
            if (data.status === "completed") {
              clearInterval(intervalId);
              setProcessing(false);
              setTaskId(null);
              setResults(data.result);
              handleSuccess("批量导入完成");
              
              // 创建结果CSV下载链接
              const csvBlob = new Blob([data.result.csv_result], { type: "text/csv;charset=utf-8;" });
              const csvUrl = URL.createObjectURL(csvBlob);
              setDownloadUrl(csvUrl);
              
              // 创建密码信息CSV下载链接
              const passwordBlob = new Blob([data.result.password_info], { type: "text/csv;charset=utf-8;" });
              const passwordUrl = URL.createObjectURL(passwordBlob);
              setPasswordDownloadUrl(passwordUrl);
              
              // 刷新任务列表
              fetchTasks();
            } else if (data.status === "failed") {
              clearInterval(intervalId);
              setProcessing(false);
              setTaskId(null);
              handleError(data.error || "导入失败");
              // 刷新任务列表
              fetchTasks();
            } else if (data.status === "processing") {
              setProgress(data.progress || 0);
            }
          } else {
            clearInterval(intervalId);
            setProcessing(false);
            setTaskId(null);
            const errorData = await response.json();
            handleError(errorData.detail || "检查任务状态失败");
          }
        } catch (err) {
          console.error("检查任务状态错误:", err);
          clearInterval(intervalId);
          setProcessing(false);
          setTaskId(null);
          handleError("检查任务状态时发生错误: " + err.message);
        }
      }, 2000); // 每2秒检查一次状态
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [taskId, processing, token, setSuccess, setError]);

  // 获取任务列表
  const fetchTasks = async (page = 1) => {
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/scripts/batch-tasks?page=${page}&size=${pagination.size}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
        setPagination({
          page: data.page,
          size: data.size,
          total: data.total,
          hasNext: data.has_next,
          hasPrev: data.has_prev
        });
      } else {
        const errorData = await response.json();
        handleError(errorData.detail || "获取任务列表失败");
      }
    } catch (err) {
      console.error("获取任务列表错误:", err);
      handleError("获取任务列表时发生错误: " + err.message);
    } finally {
      setLoadingTasks(false);
    }
  };

  // 查询任务状态
  const checkTaskStatus = async (taskId) => {
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/scripts/batch-import-status/${taskId}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // 刷新任务列表
        fetchTasks(pagination.page);
        return data;
      } else {
        const errorData = await response.json();
        handleError(errorData.detail || "获取任务状态失败");
      }
    } catch (err) {
      console.error("获取任务状态错误:", err);
      handleError("获取任务状态时发生错误: " + err.message);
    }
  };

  // 获取任务详情
  const fetchTaskDetail = async (taskId) => {
    setLoadingTaskDetail(true);
    setTaskDetail(null);
    try {
      const response = await enhancedFetch(`${API_BASE_URL}/scripts/batch-import-status/${taskId}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTaskDetail({ taskId, ...data });
      } else {
        const errorData = await response.json();
        handleError(errorData.detail || "获取任务详情失败");
      }
    } catch (err) {
      console.error("获取任务详情错误:", err);
      handleError("获取任务详情时发生错误: " + err.message);
    } finally {
      setLoadingTaskDetail(false);
    }
  };

  // 切换到任务列表标签页时获取任务列表
  useEffect(() => {
    if (activeTab === 'tasks') {
      setLoadingTasks(true);
      fetchTasks(1);
    }
  }, [activeTab, token]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && !selectedFile.name.endsWith(".csv")) {
      handleError("请选择CSV文件");
      return;
    }
    setFile(selectedFile);
    setResults(null);
    setDownloadUrl(null);
    setPasswordDownloadUrl(null);
    setProgress(0);
  };

  const handleUpload = async () => {
    if (!file) {
      handleError("请选择一个CSV文件");
      return;
    }

    setUploading(true);
    setProcessing(true);
    handleError(null);
    handleSuccess(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await enhancedFetch(`${API_BASE_URL}/scripts/batch-import-nodes/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setTaskId(data.task_id);
        handleSuccess(data.message);
      } else {
        const errorData = await response.json();
        handleError(errorData.detail || "导入失败");
        setProcessing(false);
      }
    } catch (err) {
      console.error("上传错误:", err);
      handleError("上传文件时发生错误: " + err.message);
      setProcessing(false);
    } finally {
      setUploading(false);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = "username,wallet_address,tenant_name,pubkey,region,rental_time\nuser1,0x1234567890123456789012345678901234567890,Tenant1,0xpubkey,uc-洛杉矶,2025/08/08\nuser2,0x0987654321098765432109876543210987654321,Tenant2,0xpubkey,uc-洛杉矶,2025/08/09\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "batch_import_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 处理分页
  const handlePageChange = (newPage) => {
    setLoadingTasks(true);
    fetchTasks(newPage);
  };

  // 返回任务列表
  const backToTaskList = () => {
    setTaskDetail(null);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>批量导入用户和节点</h2>
        <p>通过CSV文件批量创建用户、生成节点并分配节点</p>
      </div>
      
      {/* 标签页切换 */}
      {!taskDetail && (
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            批量导入
          </button>
          <button 
            className={`tab-button ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            批量任务列表
          </button>
        </div>
      )}
      
      {/* 批量导入标签页内容 */}
      {activeTab === 'import' && !taskDetail && (
        <div className="tab-content">
          <div className="form-group">
            <label htmlFor="csvFile">选择CSV文件:</label>
            <input
              type="file"
              id="csvFile"
              accept=".csv"
              onChange={handleFileChange}
              disabled={uploading || processing}
              className="form-control"
            />
          </div>

          {processing && (
            <div className="progress-section" style={{ marginTop: "20px" }}>
              <h3>处理中...</h3>
              <div className="progress-bar-container" style={{ 
                width: "100%", 
                backgroundColor: "#f0f0f0", 
                borderRadius: "4px", 
                overflow: "hidden" 
              }}>
                <div className="progress-bar" style={{ 
                  width: `${progress}%`, 
                  backgroundColor: "#007bff", 
                  height: "20px", 
                  transition: "width 0.3s ease"
                }}></div>
              </div>
              <p style={{ textAlign: "center", marginTop: "10px" }}>{progress}% 完成</p>
            </div>
          )}

          <div className="form-actions">
            <button 
              onClick={downloadCSVTemplate}
              className="secondary-button"
              disabled={uploading || processing}
            >
              下载CSV模板
            </button>
            <button
              onClick={handleUpload}
              className="primary-button"
              disabled={uploading || processing || !file}
            >
              {uploading || processing ? "处理中..." : "开始导入"}
            </button>
          </div>

          {results && (
            <div className="results-section" style={{ marginTop: "20px" }}>
              <h3>导入结果</h3>
              <div className="result-summary">
                <p>总共处理: {results.results.length} 条记录</p>
                <p>成功创建用户: {results.created_users_count} 个</p>
              </div>

              <div className="result-table-container" style={{ overflowX: "auto", marginTop: "15px" }}>
                <table className="results-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>行号</th>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>用户名</th>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>钱包地址</th>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>租户名称</th>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>公钥</th>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>状态</th>
                      <th style={{ border: "1px solid #ddd", padding: "12px" }}>消息</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((result, index) => (
                      <tr 
                        key={index} 
                        style={{ 
                          backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                          borderBottom: "1px solid #eee"
                        }}
                      >
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.line}</td>
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.username}</td>
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.wallet_address}</td>
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.tenant_name || ''}</td>
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                          {result.pubkey ? `${result.pubkey.substring(0, 10)}...${result.pubkey.substring(result.pubkey.length - 10)}` : ''}
                        </td>
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                          <span 
                            className={`status-badge ${
                              result.status === "success" ? "status-success" : 
                              result.status === "warning" ? "status-partial" : "status-error"
                            }`}
                          >
                            {result.status}
                          </span>
                        </td>
                        <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="download-section" style={{ marginTop: "20px" }}>
                <h4>下载结果文件</h4>
                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <a
                    href={downloadUrl}
                    download="batch_import_results.csv"
                    className="primary-button"
                    style={{ textDecoration: "none" }}
                  >
                    下载导入结果CSV
                  </a>
                  <a
                    href={passwordDownloadUrl}
                    download="new_users_passwords.csv"
                    className="secondary-button"
                    style={{ textDecoration: "none" }}
                  >
                    下载新用户密码CSV
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 批量任务列表标签页内容 */}
      {activeTab === 'tasks' && !taskDetail && (
        <div className="tab-content batch-task-list">
          <div className="batch-task-header">
            <h2>批量任务列表</h2>
            <button onClick={fetchTasks} className="refresh-button">刷新</button>
          </div>
          
          {loadingTasks ? (
            <div className="loading">加载中...</div>
          ) : tasks.length === 0 ? (
            <div className="no-tasks">暂无批量任务</div>
          ) : (
            <>
              <div className="tasks-container">
                {tasks.map(task => (
                  <div key={task.id} className="task-item">
                    <div className="task-info">
                      <div className="task-id">任务ID: {task.id}</div>
                      {task.filename && (
                        <div className="task-filename">文件名: {task.filename}</div>
                      )}
                      <div className="task-status">
                        状态: 
                        <span className={`status-${task.status}`}>
                          {task.status === 'processing' && '处理中'}
                          {task.status === 'completed' && '已完成'}
                          {task.status === 'failed' && '失败'}
                        </span>
                      </div>
                      {task.status === 'processing' && (
                        <div className="task-progress">
                          进度: {task.progress || 0}%
                        </div>
                      )}
                      <div className="task-created">
                        创建时间: {new Date(task.created_at * 1000).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="task-actions">
                      {task.status === 'completed' && (
                        <button 
                          onClick={() => fetchTaskDetail(task.id)}
                          className="retry-button"
                        >
                          查看详情
                        </button>
                      )}
                      {task.status === 'failed' && (
                        <button 
                          onClick={() => checkTaskStatus(task.id)}
                          className="retry-button"
                        >
                          重试
                        </button>
                      )}
                      {task.status === 'processing' && (
                        <button 
                          onClick={() => checkTaskStatus(task.id)}
                          className="retry-button"
                        >
                          刷新状态
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* 分页控件 */}
              <div className="pagination">
                <button 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                  className="pagination-button"
                >
                  上一页
                </button>
                
                <span className="pagination-info">
                  第 {pagination.page} 页，共 {Math.ceil(pagination.total / pagination.size)} 页，总计 {pagination.total} 条记录
                </span>
                
                <button 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                  className="pagination-button"
                >
                  下一页
                </button>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* 任务详情视图 */}
      {taskDetail && (
        <div className="tab-content task-detail">
          <div className="task-detail-header">
            <button onClick={backToTaskList} className="secondary-button">返回任务列表</button>
            <h2>任务详情 - {taskDetail.taskId}</h2>
          </div>
          
          {loadingTaskDetail ? (
            <div className="loading">加载中...</div>
          ) : (
            <div className="task-detail-content">
              <div className="task-info-section">
                <h3>任务信息</h3>
                <div className="task-info-item">
                  <strong>任务ID:</strong> {taskDetail.taskId}
                </div>
                <div className="task-info-item">
                  <strong>状态:</strong> 
                  <span className={`status-${taskDetail.status}`}>
                    {taskDetail.status === 'processing' && '处理中'}
                    {taskDetail.status === 'completed' && '已完成'}
                    {taskDetail.status === 'failed' && '失败'}
                  </span>
                </div>
                {taskDetail.progress !== undefined && (
                  <div className="task-info-item">
                    <strong>进度:</strong> {taskDetail.progress}%
                  </div>
                )}
                {taskDetail.result && (
                  <>
                    <div className="task-info-item">
                      <strong>处理记录数:</strong> {taskDetail.result.results.length}
                    </div>
                    <div className="task-info-item">
                      <strong>创建用户数:</strong> {taskDetail.result.created_users_count}
                    </div>
                  </>
                )}
              </div>
              
              {taskDetail.status === 'completed' && taskDetail.result && (
                <div className="task-results-section">
                  <h3>处理结果</h3>
                  <div className="result-table-container" style={{ overflowX: "auto", marginTop: "15px" }}>
                    <table className="results-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#f8f9fa" }}>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>行号</th>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>用户名</th>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>钱包地址</th>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>租户名称</th>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>公钥</th>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>状态</th>
                          <th style={{ border: "1px solid #ddd", padding: "12px" }}>消息</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taskDetail.result.results.map((result, index) => (
                          <tr 
                            key={index} 
                            style={{ 
                              backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                              borderBottom: "1px solid #eee"
                            }}
                          >
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.line}</td>
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.username}</td>
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.wallet_address}</td>
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.tenant_name || ''}</td>
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                              {result.pubkey ? `${result.pubkey.substring(0, 10)}...${result.pubkey.substring(result.pubkey.length - 10)}` : ''}
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                              <span 
                                className={`status-badge ${
                                  result.status === "success" ? "status-success" : 
                                  result.status === "warning" ? "status-partial" : "status-error"
                                }`}
                              >
                                {result.status}
                              </span>
                            </td>
                            <td style={{ border: "1px solid #ddd", padding: "12px" }}>{result.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="download-section" style={{ marginTop: "20px" }}>
                    <h4>下载结果文件</h4>
                    <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                      <button
                        onClick={() => {
                          const csvBlob = new Blob([taskDetail.result.csv_result], { type: "text/csv;charset=utf-8;" });
                          const csvUrl = URL.createObjectURL(csvBlob);
                          const link = document.createElement("a");
                          link.href = csvUrl;
                          link.download = `batch_import_results_${taskDetail.taskId}.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="primary-button"
                      >
                        下载导入结果CSV
                      </button>
                      <button
                        onClick={() => {
                          const passwordBlob = new Blob([taskDetail.result.password_info], { type: "text/csv;charset=utf-8;" });
                          const passwordUrl = URL.createObjectURL(passwordBlob);
                          const link = document.createElement("a");
                          link.href = passwordUrl;
                          link.download = `new_users_passwords_${taskDetail.taskId}.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="secondary-button"
                      >
                        下载新用户密码CSV
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {taskDetail.status === 'failed' && taskDetail.error && (
                <div className="task-error-section">
                  <h3>错误信息</h3>
                  <div className="error-message-box">
                    <pre>{taskDetail.error}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchImport;